import React from 'react'
import { observer, inject } from 'mobx-react'
import { LatLngBounds, Point } from 'leaflet'
import SearchIcon from '../../common/icons/SearchIcon'
import AddMarkerIcon from '../../common/icons/AddMarkerIcon'
import constants, { PLACES_REQUEST_FIELDS, selectionTypes } from '../../../constants'
import api from '../../../api'

// Shared function to perform query search (used by both prediction click and search button/Enter)
const performQuerySearch = async (queryText, uiStore, dataStore, selectionStore) => {
  const poi = dataStore && dataStore.poi
  const preserved = poi && poi.captureClassificationState ? poi.captureClassificationState() : null
  try {
    // Ensure POI menu is open to display results
    if (uiStore && !uiStore.isPOIMenuExpanded) {
      uiStore.isPOIMenuExpanded = true
    }
    // Get map bounds and perform a single API call with rectangular bounds
    // Places Text Search supports rectangular bounds, so we don't need the 9-cell grid approach
    let allPlaces = []
    let visibleBounds = null
    let mapBounds = null
    const polygonBounds = null

    // Check if a shape is selected (GEOPOLYGON, POINT_BUFFER, LINE, or BOUNDARY)
    const selectedShape = selectionStore && selectionStore.item
    const isShapeSelected = selectedShape && selectedShape.geoJSON && (
      selectionStore.type === selectionTypes.GEOPOLYGON ||
      selectionStore.type === selectionTypes.POINT_BUFFER ||
      selectionStore.type === selectionTypes.LINE ||
      selectionStore.type === selectionTypes.BOUNDARY
    )

    if (isShapeSelected && typeof selectedShape.bounds === 'function') {
      // Use shape bounds for search
      const shapeBounds = selectedShape.bounds()
      if (shapeBounds && shapeBounds[0] && shapeBounds[1]) {
        const sw = { lat: shapeBounds[0][0], lng: shapeBounds[0][1] }
        const ne = { lat: shapeBounds[1][0], lng: shapeBounds[1][1] }
        // Store visible bounds for filtering
        visibleBounds = {
          ne: { lat: ne.lat, lng: ne.lng },
          sw: { lat: sw.lat, lng: sw.lng }
        }
        // Build rectangular bounds from shape for the API call
        mapBounds = {
          rectangle: {
            low: {
              latitude: sw.lat,
              longitude: sw.lng
            },
            high: {
              latitude: ne.lat,
              longitude: ne.lng
            }
          }
        }
        // Store bounds for visualization rectangle
        if (uiStore) {
          uiStore.poiSearchBoundsRectangle = { sw, ne }
        }
      }
    } else if (uiStore && uiStore.map && uiStore.map.leafletElement) {
      // Use visible map area (between sidebars) instead of full map bounds
      // This ensures results appear in the center of visible map, not hidden under sidebars
      const leafletElement = uiStore.map.leafletElement
      const bounds = leafletElement.getBounds()
      if (bounds && bounds.isValid()) {
        // Get visible map area (accounting for sidebars)
        const size = leafletElement.getSize()
        let paddingTopLeft = uiStore.boundsTopLeftOffset || { x: 0, y: 0 }
        const paddingBottomRight = uiStore.boundsBottomRightOffset || { x: 0, y: 0 }

        // Fallback: If boundsTopLeftOffset is not set or is (0,0), calculate left sidebar width directly
        // This handles the case when the page initially loads and offsets haven't been calculated yet
        if ((!paddingTopLeft.x && !paddingTopLeft.y) || (paddingTopLeft.x === 0 && paddingTopLeft.y === 0)) {
          const leftSidebarWidth = uiStore.menuWidth || uiStore.toolsWidth || 332 // Default to 332px if not available
          paddingTopLeft = { x: leftSidebarWidth, y: 0 }
        }

        // Calculate right sidebar (POI menu) width
        const windowWidth = window.innerWidth || size.x
        const poiMenuWidth = uiStore.isPOIMenuExpanded
          ? Math.min(windowWidth * 0.29, 555) // 29vw with max 555px
          : 56 // Button strip width when collapsed

        // Calculate the actual visible bounds (the area between sidebars)
        const visibleTopLeft = new Point(paddingTopLeft.x, paddingTopLeft.y)
        const visibleBottomRight = new Point(
          size.x - paddingBottomRight.x - poiMenuWidth,
          size.y - paddingBottomRight.y
        )

        // Convert visible area corners to lat/lng
        const visibleTopLeftLatLng = leafletElement.containerPointToLatLng(visibleTopLeft)
        const visibleBottomRightLatLng = leafletElement.containerPointToLatLng(visibleBottomRight)

        // Create visible bounds from the actual visible area (not full map)
        const visibleNe = {
          lat: Math.max(visibleTopLeftLatLng.lat, visibleBottomRightLatLng.lat),
          lng: Math.max(visibleTopLeftLatLng.lng, visibleBottomRightLatLng.lng)
        }
        const visibleSw = {
          lat: Math.min(visibleTopLeftLatLng.lat, visibleBottomRightLatLng.lat),
          lng: Math.min(visibleTopLeftLatLng.lng, visibleBottomRightLatLng.lng)
        }

        // ==================== CLAMP TO MAXIMUM BOUNDS ====================
        const MAX_LAT_SPAN = 2.0 // Maximum latitude span (~220 km)
        const MAX_LNG_SPAN = 2.0 // Maximum longitude span (~220 km)

        // Calculate current span
        let latSpan = visibleNe.lat - visibleSw.lat
        let lngSpan = visibleNe.lng - visibleSw.lng

        // If span exceeds maximum, clamp to max and center around current view
        if (latSpan > MAX_LAT_SPAN) {
          const centerLat = (visibleNe.lat + visibleSw.lat) / 2
          visibleNe.lat = centerLat + (MAX_LAT_SPAN / 2)
          visibleSw.lat = centerLat - (MAX_LAT_SPAN / 2)
          latSpan = MAX_LAT_SPAN
          console.warn(`Latitude span clamped to ${MAX_LAT_SPAN}°`)
        }

        if (lngSpan > MAX_LNG_SPAN) {
          const centerLng = (visibleNe.lng + visibleSw.lng) / 2
          visibleNe.lng = centerLng + (MAX_LNG_SPAN / 2)
          visibleSw.lng = centerLng - (MAX_LNG_SPAN / 2)
          lngSpan = MAX_LNG_SPAN
          console.warn(`Longitude span clamped to ${MAX_LNG_SPAN}°`)
        }

        visibleNe.lat = Math.min(90, Math.max(-90, visibleNe.lat))
        visibleSw.lat = Math.min(90, Math.max(-90, visibleSw.lat))
        visibleNe.lng = Math.min(180, Math.max(-180, visibleNe.lng))
        visibleSw.lng = Math.min(180, Math.max(-180, visibleSw.lng))

        // Calculate bounds dimensions for margin (to ensure rectangle stays well within visible area)
        const latDiff = visibleNe.lat - visibleSw.lat
        const lngDiff = visibleNe.lng - visibleSw.lng

        // Add a larger margin (15% of bounds size) to ensure rectangle is clearly within visible area
        // This prevents the rectangle from extending under sidebars
        const latMargin = latDiff * 0.15
        const lngMargin = lngDiff * 0.15

        // Create smaller bounds for visualization (centered in visible area with margin)
        const rectNe = {
          lat: visibleNe.lat - latMargin,
          lng: visibleNe.lng - lngMargin
        }
        const rectSw = {
          lat: visibleSw.lat + latMargin,
          lng: visibleSw.lng + lngMargin
        }

        // Store visible bounds for filtering results (using actual visible area)
        visibleBounds = {
          ne: { lat: visibleNe.lat, lng: visibleNe.lng },
          sw: { lat: visibleSw.lat, lng: visibleSw.lng }
        }

        // Build rectangular bounds for the API call (using visible bounds)
        mapBounds = {
          rectangle: {
            low: {
              latitude: visibleSw.lat,
              longitude: visibleSw.lng
            },
            high: {
              latitude: visibleNe.lat,
              longitude: visibleNe.lng
            }
          }
        }

        // Store bounds for visualization rectangle (using smaller bounds with margin)
        // This ensures the rectangle is clearly visible and doesn't extend under sidebars
        if (uiStore) {
          uiStore.poiSearchBoundsRectangle = {
            sw: { lat: rectSw.lat, lng: rectSw.lng },
            ne: { lat: rectNe.lat, lng: rectNe.lng }
          }
        }
      }
    }

    // Perform a single API call with rectangular bounds
    if (mapBounds) {
      // Reset limit exceeded flag before new search
      if (dataStore && dataStore.poi) {
        dataStore.poi.isLimitExceeded = false
        dataStore.poi.limitExceededMessage = ''
      }
      if (uiStore) {
        uiStore.isLimitExceeded = false
        uiStore.limitExceededMessage = ''
      }
      try {
        const places = await api.fetchPlacesSearchByText(
          queryText,
          constants.GOOGLE_MAPS_KEY,
          PLACES_REQUEST_FIELDS,
          polygonBounds, // polygonBounds (null if not using shape polygon)
          mapBounds, // mapBounds - rectangular bounds
          20 // maxResultCount: limit to 20 results
        )
        // Filter results to ensure they're within visible bounds with margin
        if (places && Array.isArray(places) && visibleBounds) {
          allPlaces = places.filter(place => {
            if (!place.location || !place.location.latitude || !place.location.longitude) {
              return false
            }
            const placeLat = place.location.latitude
            const placeLng = place.location.longitude
            return placeLat >= visibleBounds.sw.lat && placeLat <= visibleBounds.ne.lat &&
              placeLng >= visibleBounds.sw.lng && placeLng <= visibleBounds.ne.lng
          }).slice(0, 20)
        } else {
          allPlaces = (places || []).slice(0, 20)
        }
      } catch (error) {
        console.warn('Error in text search:', error)
        // Handle limit exceeded errors
        // Check for limit_exceeded flag or error message containing limit-related keywords
        const isLimitError = error.isLimitExceeded ||
          (error.message && (
            error.message.includes('limit') ||
            error.message.includes('maximum') ||
            error.message.includes('reached')
          ))

        if (isLimitError) {
          // Error message already contains user-friendly text with HTML links
          console.warn('POI text search limit exceeded:', error.message)
          // Set limit exceeded flag and message on stores to show UpgradeToProCard
          if (dataStore && dataStore.poi) {
            dataStore.poi.isLimitExceeded = true
            dataStore.poi.limitExceededMessage = error.message || ''
          }
          if (uiStore) {
            uiStore.isLimitExceeded = true
            uiStore.limitExceededMessage = error.message || ''
          }
          // Results will be empty, which is fine
        } else {
          console.warn('Error fetching places by text:', error)
          // Show generic error notification
          if (uiStore && uiStore.setStatusMessage) {
            uiStore.setStatusMessage(
              'Failed to search places. Please try again.',
              'error',
              5000
            )
          }
        }

        allPlaces = []
      }
    }
    if (!allPlaces || allPlaces.length === 0) {
      dataStore.poi.placeResults = []

      // Check if a target group is selected
      const isTargetGroupSelected = selectionStore && selectionStore.type === selectionTypes.TARGET_GROUP
      if (isTargetGroupSelected && selectionStore.item) {
        // If we have a target group, ensure its saved markers are shown
        // This handles cases where API limit is reached but we still want to show saved items
        if (uiStore && uiStore.populatePoiResultsForCurrentSelection) {
          await uiStore.populatePoiResultsForCurrentSelection()
        }
      } else if (poi && poi.replaceTemporaryMarkersPreservingClassified) {
        // No results for this query, but keep already-classified bucket items
        poi.replaceTemporaryMarkersPreservingClassified([])
      } else {
        dataStore.poi.temporaryMarkers = []
      }

      // Clear visualization rectangle when no results
      if (uiStore) {
        uiStore.poiSearchBoundsRectangle = null
      }
      return
    }

    // Save original search bounds BEFORE zooming (for "Search this area" feature)
    // This must happen before setBounds is called to avoid race conditions
    // Use visible bounds (the actual area searched) instead of full map bounds
    if (uiStore && visibleBounds) {
      uiStore.poiSearchOriginalBounds = new LatLngBounds(
        { lat: visibleBounds.sw.lat, lng: visibleBounds.sw.lng },
        { lat: visibleBounds.ne.lat, lng: visibleBounds.ne.lng }
      )
      uiStore.showSearchThisAreaButton = false // Hide button initially after new search
      uiStore.isNearbySearchActive = false // Clear nearby search flag for text search
      uiStore.searchButtonWasShown = false // Reset flag when new search is performed
    }

    // Use POI's serializePlaces to format (same as searchNearbyPlaces)
    // This also keeps already-classified bucket items instead of replacing the whole list
    const placeResults = dataStore.poi.serializePlaces(allPlaces)
    dataStore.poi.placeResults = placeResults
    // Check if a target group is selected - if so, populate all its markers
    const isTargetGroupSelected = selectionStore && selectionStore.type === selectionTypes.TARGET_GROUP
    if (isTargetGroupSelected && selectionStore.item) {
      // Populate all markers from the target group and merge with search results
      await uiStore.populatePoiResultsForCurrentSelection()
      // Merge search results with existing markers (avoid duplicates)
      const existingIds = new Set(dataStore.poi.temporaryMarkers.map(m => m.id))
      allPlaces.forEach(place => {
        if (!place.location) return
        const markerId = place.id || `${place.location.latitude},${place.location.longitude}`
        if (!existingIds.has(markerId)) {
          const newMarker = {
            id: markerId,
            position: { latLng: { lat: place.location.latitude, lng: place.location.longitude } },
            name: (place.displayName && place.displayName.text) || '',
            address: place.formattedAddress || '',
            type: Array.isArray(place.types) && place.types.length ? place.types[0] : '',
            types: place.types || [],
            getColor: 7,
            icon_url: place.iconMaskBaseUri || null
          }
          dataStore.poi.temporaryMarkers.push(newMarker)
          existingIds.add(markerId)
        }
      })
      // Update placeResults to include all markers
      dataStore.poi.placeResults = placeResults
    }
    // Update search bar with the query text
    uiStore.poiSearchQuery = queryText
    dataStore.poi.clearPredictions()
  } catch (e) {
    console.error('Error in performQuerySearch:', e)
    if (poi && poi.replaceTemporaryMarkersPreservingClassified) {
      // Search failed; still keep items already classified into buckets
      poi.replaceTemporaryMarkersPreservingClassified([])
    } else if (poi) {
      poi.temporaryMarkers = []
    }
  } finally {
    if (poi && poi.restoreClassificationState) {
      poi.restoreClassificationState(preserved)
    }
  }
}

const PoiAutocomplete = ({ uiStore, dataStore, selectionStore }) => {
  // Handles clicking on a place prediction (fetch details by placeId)
  const handlePlacePredictionClick = async (prediction) => {
    const poi = dataStore && dataStore.poi
    const preserved = poi && poi.captureClassificationState ? poi.captureClassificationState() : null
    try {
      const details = await api.fetchPlaceDetails(
        prediction.placeId,
        constants.GOOGLE_MAPS_KEY,
        PLACES_REQUEST_FIELDS
      )
      // Ensure we always have a valid id (use place_id or fallback to lat/lng)
      const markerId = details.place_id ||
        (details.latitude && details.longitude
          ? `${details.latitude},${details.longitude}`
          : `marker_${Date.now()}_${Math.random().toString(36).slice(2)}`)
      const newMarker = {
        id: markerId,
        position: { latLng: { lat: details.latitude, lng: details.longitude } },
        name: details.name || '',
        address: details.formattedAddress || '',
        type: Array.isArray(details.types) && details.types.length ? details.types[0] : '',
        types: details.types || [],
        getColor: 7,
        icon_url: details.icon_url || details.iconMaskBaseUri || null
      }
      // Check if a target group is selected - if so, populate all its markers
      const isTargetGroupSelected = selectionStore && selectionStore.type === selectionTypes.TARGET_GROUP
      if (isTargetGroupSelected && selectionStore.item) {
        // Populate all markers from the target group
        await uiStore.populatePoiResultsForCurrentSelection()
        // Add the new marker if it's not already in the list
        const existingMarker = dataStore.poi.temporaryMarkers.find(m => m.id === markerId)
        if (!existingMarker) {
          dataStore.poi.temporaryMarkers.push(newMarker)
        }
      } else if (poi && poi.replaceTemporaryMarkersPreservingClassified) {
        poi.replaceTemporaryMarkersPreservingClassified([newMarker])
      } else {
        dataStore.poi.temporaryMarkers = [newMarker]
      }
      // Update search bar with the selected prediction text
      uiStore.poiSearchQuery = prediction.text.text
      dataStore.poi.clearPredictions()
      // Zoom to the marker that was just created
      if (uiStore.map && details.latitude && details.longitude) {
        uiStore.map.leafletElement.setView([details.latitude, details.longitude], 15)
      }
    } catch (e) {
      if (poi && poi.replaceTemporaryMarkersPreservingClassified) {
        poi.replaceTemporaryMarkersPreservingClassified([])
      } else if (poi) {
        poi.temporaryMarkers = []
      }
    } finally {
      if (poi && poi.restoreClassificationState) {
        poi.restoreClassificationState(preserved)
      }
    }
  }

  // Handles clicking on a query prediction (search by text)
  const handleQueryPredictionClick = async (prediction) => {
    const queryText = prediction.text.text
    await performQuerySearch(queryText, uiStore, dataStore, selectionStore)
  }

  return (
    (dataStore.poi.placePredictions.length > 0 || dataStore.poi.queryPredictions.length > 0) && (
      <div className='poi__search-predictions'>
        <ul className='poi__place-predictions'>
          {
          dataStore.poi.placePredictions.map(place => (
            <li className='poi__prediction-item' key={place.placeId}>
              <button className='poi__prediction' onClick={() => handlePlacePredictionClick(place)}>
                <AddMarkerIcon className='poi__icon poi__icon--marker' />
                {place.text.text}
              </button>
            </li>
          ))
        }
        </ul>
        <ul className='poi__query-predictions'>
          {
          dataStore.poi.queryPredictions.map((query, idx) => (
            <li className='poi__prediction-item' key={idx}>
              <button className='poi__prediction' onClick={() => handleQueryPredictionClick(query)}>
                <SearchIcon className='poi__icon' />
                {query.text.text}
              </button>
            </li>
          ))
        }
        </ul>
      </div>
    )
  )
}

// Export the function so it can be used by PointOfInterest component
export { performQuerySearch }

export default inject('uiStore', 'dataStore', 'selectionStore')(observer(PoiAutocomplete))
